import {GridFilterModel, GridLogicOperator} from '@mui/x-data-grid-pro';
import {useEffect, useRef, useState} from 'react';

import {
  getCachedByKeyWithExpiry,
  setCacheByKeyWithExpiry,
} from '../../browserCacheUtils';
import {useEntityProject} from '../../context';
import {makeDateFilter} from '../../filters/common';
import {getValidFilterModel} from '../../grid/filters';
import {queryToGridFilterModel} from '../SavedViews/savedViewUtil';
import {SavedViewDefinition} from '../wfReactInterface/generatedBuiltinObjectClasses.zod';
import {TraceServerClient} from '../wfReactInterface/traceServerClient';
import {useGetTraceServerClientContext} from '../wfReactInterface/traceServerClientContext';
import {projectIdFromParts} from '../wfReactInterface/tsDataModelHooks';
import {convertFilterModelToDatetimeFilters} from './common';

const CACHE_KEY_PREFIX = 'weave_threads_datetime_filter_';
const CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 1 day

const threadsDatetimeFilterCacheKey = (
  entity: string,
  project: string
): string => {
  return `${CACHE_KEY_PREFIX}${entity}_${project}`;
};

async function getDefaultThreadsFilter(
  tsCient: TraceServerClient,
  entity: string,
  project: string
) {
  // Check cache first
  const cacheKey = threadsDatetimeFilterCacheKey(entity, project);
  const cachedFilter = getCachedByKeyWithExpiry<GridFilterModel>(
    cacheKey,
    CACHE_EXPIRY_MS
  );

  if (cachedFilter) {
    return cachedFilter;
  }

  // Calculate filter if not cached
  const computedFilter = await calculateDefaultThreadsFilter(
    tsCient,
    entity,
    project
  );

  // Cache the computed filter
  setCacheByKeyWithExpiry(cacheKey, computedFilter);

  return computedFilter;
}

async function calculateDefaultThreadsFilter(
  tsCient: TraceServerClient,
  entity: string,
  project: string
) {
  // Make at most 2 attempts to get the default filter, once for the last 7 days, once for the last 30 days
  let getNumberOfRecordsAfterDays = async (days: number) => {
    let dataTimeFilters = convertFilterModelToDatetimeFilters({
      items: [makeDateFilter(days)],
    });
    let result = await tsCient.threadsStreamQuery({
      project_id: projectIdFromParts({entity, project}),
      filter: {
        after_datetime: dataTimeFilters.after_datetime,
      },
    });
    return result.threads.length;
  };

  // If there are more than 50 threads, return a filter for the last 7 days
  if ((await getNumberOfRecordsAfterDays(7)) > 50) {
    return {
      items: [makeDateFilter(7)],
      logicOperator: GridLogicOperator.And,
    };
  }

  // If there are more than 50 threads, return a filter for the last 30 days
  if ((await getNumberOfRecordsAfterDays(30)) > 50) {
    return {
      items: [makeDateFilter(30)],
      logicOperator: GridLogicOperator.And,
    };
  }

  return {
    items: [makeDateFilter(180)],
    logicOperator: GridLogicOperator.And,
  };
}

export const useThreadFilterHooks = (
  query: Record<string, string>,
  viewDef: SavedViewDefinition
) => {
  // This is a signal to indicate whether the default filter generation process has been completed.
  // We only want to generate the default filter once, after that all subsequent changes to the
  // filter should be applied immediately.
  const defaultFilterAdopted = useRef(false);
  const getTsClient = useGetTraceServerClientContext();

  const {entity, project} = useEntityProject();

  const [filterModel, setFilterModel] = useState<GridFilterModel | null>(null);

  useEffect(() => {
    let filterCandidate: GridFilterModel | null = null;
    if (query.filters) {
      filterCandidate = getValidFilterModel(query.filters);
    } else if (viewDef.query) {
      filterCandidate = queryToGridFilterModel(viewDef.query);
    }

    const isFilterCandidateEmpty =
      !filterCandidate || filterCandidate.items.length === 0;

    if (!isFilterCandidateEmpty) {
      setFilterModel(filterCandidate);
      return;
    }
    if (!defaultFilterAdopted.current) {
      defaultFilterAdopted.current = true;
      // load the default filter
      (async () => {
        const defaultFilter = await getDefaultThreadsFilter(
          getTsClient(),
          entity,
          project
        );
        setFilterModel(defaultFilter);
      })();
      return;
    }
    // if the filter candidate is empty and the default filter has been adopted, return an empty filter
    // (this means the user intentionally cleared the filter)
    setFilterModel({items: []});
  }, [query.filters, viewDef.query, getTsClient, entity, project]);

  return {
    filterModel,
  };
};
